import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import Layout from './components/Layout';
import Login from './pages/Login';
import DemoBanner from './components/DemoBanner';
import { useDemoLogin } from './hooks/useDemoLogin';

// Lazy-loaded pages — each becomes its own chunk, loaded on first visit
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Contacts = lazy(() => import('./pages/Contacts'));
const Contact360 = lazy(() => import('./pages/Contact360'));
const VoiceAI = lazy(() => import('./pages/VoiceAI'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Pipeline = lazy(() => import('./pages/Pipeline'));
const Campaigns = lazy(() => import('./pages/Campaigns'));
const Inbox = lazy(() => import('./pages/Inbox'));
const Mailbox = lazy(() => import('./pages/Mailbox'));
const Billing = lazy(() => import('./pages/Billing'));
const Team = lazy(() => import('./pages/Team'));
const Settings = lazy(() => import('./pages/Settings'));
const Usage = lazy(() => import('./pages/Usage'));
const Reviews = lazy(() => import('./pages/Reviews'));
const Invoices = lazy(() => import('./pages/Invoices'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const GoogleCallback = lazy(() => import('./pages/GoogleCallback'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const PublicBooking = lazy(() => import('./pages/PublicBooking'));
const BookingManage = lazy(() => import('./pages/BookingManage'));
const SocialMedia = lazy(() => import('./pages/SocialMedia'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const BusinessDetail = lazy(() => import('./pages/admin/BusinessDetail'));
const DemoLeads = lazy(() => import('./pages/admin/DemoLeads'));
const Revenue = lazy(() => import('./pages/admin/Revenue'));
const AdminWhatsAppTemplates = lazy(() => import('./pages/admin/AdminWhatsAppTemplates'));

// Minimal loading spinner for Suspense fallback
function PageLoader() {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
    );
}

// Protected route wrapper
function ProtectedRoute({ children }) {
    const { token } = useAuthStore();
    if (!token) return <Navigate to="/login" replace />;
    return children;
}

// Agency Admin Guard
function AgencyOnly({ children }) {
    const { isAgencyAdmin, user, isLoading } = useAuthStore();
    if (isLoading) return null;
    if (!isAgencyAdmin()) {
        console.log('⛔ Redirecting to / (Not Agency Admin)', user);
        return <Navigate to="/" replace />;
    }
    return children;
}

// Business Owner Guard - redirects Platform Owner to /admin unless impersonating
function BusinessOnly({ children }) {
    const { isAgencyAdmin, isLoading, user } = useAuthStore();
    if (isLoading) return null;

    // Demo users bypass the agency redirect — they should see business views
    if (user?.role === 'demo') return children;

    const isPlatformOwner = isAgencyAdmin();
    const urlParams = new URLSearchParams(window.location.search);
    const isImpersonating = urlParams.has('business_id');

    if (isPlatformOwner && !isImpersonating) {
        console.log('🔄 Redirecting Platform Owner to /admin');
        return <Navigate to="/admin" replace />;
    }

    return children;
}

function App() {
    const { isLoading, user } = useAuthStore();
    const { pendingDemo } = useDemoLogin();

    if (isLoading || pendingDemo) {
        return (
            <div className="flex items-center justify-center h-screen bg-dark-bg text-white">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <Suspense fallback={<PageLoader />}>
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/google/callback" element={<GoogleCallback />} />
                <Route path="/book/:slug" element={<PublicBooking />} />
                <Route path="/book/manage/:token" element={<BookingManage />} />
                <Route
                    path="/"
                    element={
                        <ProtectedRoute>
                            <>
                                {user?.role === 'demo' && <DemoBanner />}
                                <Layout />
                            </>
                        </ProtectedRoute>
                    }
                >
                    {/* Business View (Default) */}
                    <Route index element={<BusinessOnly><Dashboard /></BusinessOnly>} />
                    <Route path="inbox" element={<BusinessOnly><Inbox /></BusinessOnly>} />
                    <Route path="mailbox" element={<BusinessOnly><Mailbox /></BusinessOnly>} />
                    <Route path="contacts" element={<BusinessOnly><Contacts /></BusinessOnly>} />
                    <Route path="contacts/:id" element={<BusinessOnly><Contact360 /></BusinessOnly>} />
                    <Route path="voice-ai" element={<BusinessOnly><VoiceAI /></BusinessOnly>} />
                    <Route path="calendar" element={<BusinessOnly><Calendar /></BusinessOnly>} />
                    <Route path="pipeline" element={<BusinessOnly><Pipeline /></BusinessOnly>} />
                    <Route path="campaigns" element={<BusinessOnly><Campaigns /></BusinessOnly>} />
                    <Route path="billing" element={<Billing />} />
                    <Route path="calls" element={<Navigate to="/voice-ai" replace />} />
                    <Route path="usage" element={<BusinessOnly><Usage /></BusinessOnly>} />
                    <Route path="reviews" element={<BusinessOnly><Reviews /></BusinessOnly>} />
                    <Route path="invoices" element={<BusinessOnly><Invoices /></BusinessOnly>} />
                    <Route path="social" element={<BusinessOnly><SocialMedia /></BusinessOnly>} />

                    {/* Agency View */}
                    <Route path="admin" element={<AgencyOnly><AdminDashboard /></AgencyOnly>} />
                    <Route path="admin/business/:id" element={<AgencyOnly><BusinessDetail /></AgencyOnly>} />
                    <Route path="admin/leads" element={<AgencyOnly><DemoLeads /></AgencyOnly>} />
                    <Route path="admin/revenue" element={<AgencyOnly><Revenue /></AgencyOnly>} />
                    <Route path="admin/whatsapp-templates" element={<AgencyOnly><AdminWhatsAppTemplates /></AgencyOnly>} />

                    {/* Shared */}
                    <Route path="team" element={<Team />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="help" element={<HelpCenter />} />
                </Route>

                {/* Catch all - Redirect to Home */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    );
}

export default App;
